import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Clock, ArrowRight, Loader2, Search, Tag, X, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  
  // State
  const [posts, setPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPosts, setTotalPosts] = useState(0);
  
  // Filter state from URL params
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'All');
  const [selectedTags, setSelectedTags] = useState(
    searchParams.get('tags') ? searchParams.get('tags').split(',') : []
  );
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [showAllTags, setShowAllTags] = useState(false);
  
  const postsPerPage = 12;
  const bgColor = theme.colors.backgroundDark;
  const accentColor = theme.colors.accent;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCategory !== 'All') params.set('category', selectedCategory);
    if (selectedTags.length > 0) params.set('tags', selectedTags.join(','));
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params);
  }, [selectedCategory, selectedTags, debouncedSearch, currentPage, setSearchParams]);

  // Load categories and tags on mount
  useEffect(() => {
    loadCategories();
    loadTags();
    loadFeaturedPost();
  }, []);

  // Load posts when filters change (backend-driven)
  useEffect(() => {
    loadPosts();
  }, [selectedCategory, selectedTags, debouncedSearch, currentPage]);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog/categories`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadTags = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog/tags`);
      const data = await response.json();
      setAllTags(data);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const loadFeaturedPost = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog/featured`);
      const data = await response.json();
      setFeaturedPost(data);
    } catch (error) {
      console.error('Error loading featured post:', error);
    }
  };

  const loadPosts = async () => {
    try {
      setLoading(true);
      
      // Build query params for backend filtering
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'All') {
        params.set('category', selectedCategory);
      }
      if (selectedTags.length > 0) {
        params.set('tags', selectedTags.join(','));
      }
      if (debouncedSearch) {
        params.set('search', debouncedSearch);
      }
      params.set('limit', postsPerPage.toString());
      params.set('skip', ((currentPage - 1) * postsPerPage).toString());
      
      const response = await fetch(`${BACKEND_URL}/api/blog/posts?${params.toString()}`);
      const data = await response.json();
      
      setPosts(data.posts || []);
      setTotalPosts(data.total || 0);
    } catch (error) {
      console.error('Error loading posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
    setCurrentPage(1); // Reset to first page
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1); // Reset to first page
  };

  const clearFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setSelectedTags([]);
    setSelectedCategory('All');
    setCurrentPage(1);
  };

  const hasActiveFilters = debouncedSearch || selectedTags.length > 0 || selectedCategory !== 'All';
  const totalPages = Math.ceil(totalPosts / postsPerPage);

  const handlePostClick = (slug) => {
    navigate(`/blog/${slug}`);
  };

  if (loading && posts.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bgColor }}>
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: bgColor }}>
      <SEOHead 
        title="Blog & Insights | Venkat Swaroop - Leadership & Talent Acquisition"
        description="Explore articles on leadership, HR trends, talent acquisition strategies, and recruitment insights from Venkat Swaroop, a Global Talent Acquisition Leader."
        keywords="HR blog, talent acquisition insights, recruitment articles, leadership blog, HR trends, staffing insights, Venkat Swaroop blog"
        url="https://venkatswaroop.in/blog"
      />
      <Header />
      
      <div className="container mx-auto px-6 py-20">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="mb-4 font-serif text-5xl font-bold text-white">Insights & Articles</h1>
          <div className="mx-auto h-1 w-24 rounded" style={{ backgroundColor: accentColor }}></div>
          <p className="mx-auto mt-6 max-w-3xl text-lg text-white opacity-90">
            Exploring leadership, innovation, HR trends, and talent acquisition insights
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories, Search & Tags */}
          <div className="lg:col-span-1 space-y-6">
            {/* Search Box */}
            <Card className="border-none bg-white/10 backdrop-blur-sm">
              <CardContent className="p-6">
                <h3 className="mb-4 font-serif text-xl font-bold text-white flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  Search
                </h3>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50 pr-10"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Categories */}
            <Card className="border-none bg-white/10 backdrop-blur-sm">
              <CardContent className="p-6">
                <h3 className="mb-4 font-serif text-xl font-bold text-white">Categories</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleCategoryChange('All')}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-all ${
                      selectedCategory === 'All'
                        ? 'text-white font-semibold'
                        : 'text-white/70 hover:text-white hover:bg-white/5'
                    }`}
                    style={selectedCategory === 'All' ? { backgroundColor: accentColor } : {}}
                  >
                    All Articles
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryChange(cat.name)}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-all flex justify-between items-center ${
                        selectedCategory === cat.name
                          ? 'text-white font-semibold'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                      style={selectedCategory === cat.name ? { backgroundColor: accentColor } : {}}
                    >
                      <span>{cat.name}</span>
                      <span className="text-sm">({cat.count})</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Tags Filter */}
            <Card className="border-none bg-white/10 backdrop-blur-sm">
              <CardContent className="p-6">
                <h3 className="mb-4 font-serif text-xl font-bold text-white flex items-center gap-2">
                  <Tag className="h-5 w-5" />
                  Filter by Tags
                </h3>
                
                {/* Selected Tags */}
                {selectedTags.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-white/70">Selected:</span>
                      <button
                        onClick={clearFilters}
                        className="text-xs hover:opacity-80"
                        style={{ color: accentColor }}
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedTags.map(tag => (
                        <Badge
                          key={tag}
                          className="cursor-pointer text-white border-transparent"
                          style={{ backgroundColor: accentColor }}
                          onClick={() => toggleTag(tag)}
                        >
                          {tag}
                          <X className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Available Tags */}
                <div className="flex flex-wrap gap-2">
                  {(showAllTags ? allTags : allTags.slice(0, 10)).map(({ tag, count }) => (
                    <Badge
                      key={tag}
                      variant={selectedTags.includes(tag) ? "default" : "outline"}
                      className={`cursor-pointer transition-all ${
                        selectedTags.includes(tag)
                          ? 'text-white border-transparent'
                          : 'text-white/70 border-white/30 hover:border-white/50 hover:text-white bg-transparent'
                      }`}
                      style={selectedTags.includes(tag) ? { backgroundColor: accentColor } : {}}
                      onClick={() => toggleTag(tag)}
                    >
                      {tag} ({count})
                    </Badge>
                  ))}
                </div>
                
                {allTags.length > 10 && (
                  <button
                    onClick={() => setShowAllTags(!showAllTags)}
                    className="mt-3 text-sm hover:opacity-80"
                    style={{ color: accentColor }}
                  >
                    {showAllTags ? 'Show less' : `Show all ${allTags.length} tags`}
                  </button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Active Filters Banner */}
            {hasActiveFilters && (
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 backdrop-blur-sm">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-white/70 text-sm">
                    {loading ? 'Searching...' : `Showing ${posts.length} of ${totalPosts} results`}
                  </span>
                  {debouncedSearch && (
                    <Badge variant="outline" className="text-white border-white/30">
                      Search: "{debouncedSearch}"
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setSearchQuery('')} />
                    </Badge>
                  )}
                  {selectedCategory !== 'All' && (
                    <Badge 
                      className="text-white"
                      style={{ backgroundColor: accentColor }}
                    >
                      {selectedCategory}
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => handleCategoryChange('All')} />
                    </Badge>
                  )}
                  {selectedTags.map(tag => (
                    <Badge 
                      key={tag} 
                      className="text-white"
                      style={{ backgroundColor: accentColor }}
                    >
                      {tag}
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => toggleTag(tag)} />
                    </Badge>
                  ))}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-white/70 hover:text-white"
                >
                  Clear all
                </Button>
              </div>
            )}

            {/* Featured Article */}
            {featuredPost && !hasActiveFilters && currentPage === 1 && (
              <Card 
                className="border-none bg-white/5 backdrop-blur-sm overflow-hidden cursor-pointer hover:bg-white/10 transition-all group"
                onClick={() => handlePostClick(featuredPost.slug)}
              >
                {featuredPost.featured_image && (
                  <img 
                    src={featuredPost.featured_image} 
                    alt={featuredPost.title}
                    className="w-full h-80 object-cover"
                  />
                )}
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <Badge style={{ backgroundColor: accentColor, color: bgColor }}>
                      Featured
                    </Badge>
                    <Badge variant="outline" className="text-white border-white/30">
                      {featuredPost.category}
                    </Badge>
                  </div>
                  <h2 className="font-serif text-3xl font-bold text-white mb-4 group-hover:text-opacity-90">
                    {featuredPost.title}
                  </h2>
                  <p className="text-white/80 text-lg mb-6 line-clamp-3">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center gap-6 text-white/60 text-sm">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {featuredPost.read_time} min read
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-white/50" />
              </div>
            )}

            {/* Article Grid */}
            {!loading && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {posts.filter(post => !featuredPost || post.id !== featuredPost.id || hasActiveFilters || currentPage > 1).map((post) => (
                  <Card
                    key={post.id}
                    className="border-none bg-white/5 backdrop-blur-sm overflow-hidden cursor-pointer hover:bg-white/10 transition-all group"
                    onClick={() => handlePostClick(post.slug)}
                  >
                    {post.featured_image && (
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <CardContent className="p-6">
                      <Badge variant="outline" className="mb-3 text-white border-white/30">
                        {post.category}
                      </Badge>
                      <h3 className="font-serif text-xl font-bold text-white mb-3 line-clamp-2 group-hover:text-opacity-90">
                        {post.title}
                      </h3>
                      <p className="text-white/70 text-sm mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      {/* Tags */}
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                          {post.tags.slice(0, 3).map((tag, idx) => (
                            <span 
                              key={idx}
                              className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/60"
                            >
                              {tag}
                            </span>
                          ))}
                          {post.tags.length > 3 && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/60">
                              +{post.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-white/60 text-xs">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {post.read_time} min read
                          </span>
                        </div>
                        <ArrowRight className="h-4 w-4 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {!loading && posts.length === 0 && (
              <div className="text-center py-12">
                <Search className="h-12 w-12 mx-auto text-white/30 mb-4" />
                <p className="text-white/60 text-lg mb-2">No articles found</p>
                <p className="text-white/40 text-sm mb-4">
                  {hasActiveFilters 
                    ? "Try adjusting your search or filters"
                    : "No articles in this category yet"}
                </p>
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    className="text-white border-white/30 hover:bg-white/10"
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="text-white border-white/30 hover:bg-white/10 disabled:opacity-30"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className={currentPage === pageNum 
                          ? 'text-white' 
                          : 'text-white border-white/30 hover:bg-white/10'
                        }
                        style={currentPage === pageNum ? { backgroundColor: accentColor } : {}}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="text-white border-white/30 hover:bg-white/10 disabled:opacity-30"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Post Count */}
            {!loading && posts.length > 0 && (
              <div className="text-center text-white/50 text-sm pt-4">
                Page {currentPage} of {totalPages} • {totalPosts} total articles
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogPage;
